import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVodCategories, getVodList } from '../services/api';
import { toast } from 'sonner';
import { Search, X, MoreVertical, Play, Film } from 'lucide-react';

export default function VODPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [movies, setMovies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    getVodCategories()
      .then((data) => {
        setCategories(data.categories || []);
        setTotalCount(data.total || 0);
      })
      .catch(() => {
        setCategories([]);
        setTotalCount(0);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200, skip: 0 };
    if (selectedCategory !== 'all') params.category_id = selectedCategory;
    if (searchQuery) params.search = searchQuery;

    getVodList(params)
      .then(setMovies)
      .catch((err) => {
        console.error(err);
        toast.error('Could not load movies.');
        setMovies([]);
      })
      .finally(() => setLoading(false));
  }, [selectedCategory, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const getRatingBadge = (item) => {
    const r = parseFloat(item.rating || (item.rating_5based ? item.rating_5based * 2 : 0));
    if (!r || isNaN(r) || r < 1) return null;
    return Math.round(r);
  };

  const playMovie = (movie) => {
    if (!movie.stream_url) { toast.error('Stream URL not available'); return; }
    navigate(`/watch-vod/${movie.stream_id}`, {
      state: {
        streamUrl:       movie.stream_url,
        streamUrlTs:     movie.stream_url_ts,
        streamUrlNative: movie.stream_url_native,
        title: movie.name,
        containerExtension: movie.container_extension,
        logo: movie.cover,
        isVod: true,
        chargeId: String(movie.stream_id),
      },
    });
  };

  const currentCategoryLabel = selectedCategory === 'all'
    ? 'ALL MOVIES'
    : (categories.find(c => c.category_id === selectedCategory)?.category_name || 'MOVIES').toUpperCase();

  return (
    <div className="vod-page" data-testid="vod-page">
      {/* Left Sidebar */}
      <aside className="vod-sidebar" data-testid="vod-sidebar">
        <div className="sidebar-header">
          <div className="xtream-logo">
            <img src="/logo.png" alt="FTA Xtream" style={{height:'36px', width:'auto', objectFit:'contain'}} />
          </div>
        </div>

        <nav className="sidebar-nav" data-testid="vod-category-list">
          <button
            className={`nav-item ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
            data-testid="category-all"
          >
            <span className="nav-item-name">ALL MOVIES</span>
            <span className="nav-item-count">{totalCount}</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              className={`nav-item ${selectedCategory === cat.category_id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.category_id)}
              data-testid={`category-${cat.category_id}`}
            >
              <span className="nav-item-name">{cat.category_name.toUpperCase()}</span>
              <span className="nav-item-count">{cat.count}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="vod-main" data-testid="vod-main">
        {/* Top Bar */}
        <div className="vod-top-bar">
          <div className="vod-top-spacer" />
          <h1 className="vod-category-title" data-testid="vod-category-title">
            {currentCategoryLabel}
          </h1>
          <div className="vod-top-actions">
            <button
              className="vod-icon-btn"
              onClick={() => setShowSearch(s => !s)}
              data-testid="vod-search-toggle"
            >
              <Search className="w-5 h-5" />
            </button>
            <button className="vod-icon-btn" data-testid="vod-more-btn">
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Collapsible Search */}
        {showSearch && (
          <div className="vod-search-bar">
            <form onSubmit={handleSearch} className="vod-search-form">
              <Search className="w-4 h-4" />
              <input
                type="text"
                placeholder="Search movies..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="vod-search-input"
                autoFocus
                data-testid="vod-search-input"
              />
              {searchInput && (
                <button type="button" onClick={clearSearch} className="search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </form>
          </div>
        )}

        {/* Grid */}
        <div className="vod-grid-scroll">
          {loading ? (
            <div className="vod-loading" data-testid="vod-loading">
              <div className="loading-spinner" />
              <span>Loading movies...</span>
            </div>
          ) : movies.length === 0 ? (
            <div className="vod-empty" data-testid="vod-empty">
              <Film className="w-14 h-14 mb-3" style={{opacity:0.2}} />
              <p>No movies found. Log in with your IPTV credentials to access content.</p>
            </div>
          ) : (
            <div className="vod-grid" data-testid="vod-grid">
              {movies.map((item) => {
                const badge = getRatingBadge(item);
                return (
                  <div
                    key={item.stream_id}
                    className="vod-card"
                    onClick={() => playMovie(item)}
                    data-testid={`vod-card-${item.stream_id}`}
                  >
                    <div className="vod-card-poster">
                      {item.cover ? (
                        <img src={item.cover} alt={item.name} loading="lazy" />
                      ) : (
                        <div className="vod-card-no-poster">
                          <Play className="w-8 h-8" style={{opacity:0.2}} />
                        </div>
                      )}
                      {badge && (
                        <div className="vod-rating-badge" data-testid={`vod-rating-${item.stream_id}`}>
                          {badge}
                        </div>
                      )}
                      <div className="vod-card-title-overlay">
                        <span>{item.name}</span>
                      </div>
                      <div className="vod-card-hover">
                        <div className="vod-play-circle">
                          <Play className="w-6 h-6" style={{marginLeft:'3px'}} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
